/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDSnackbar from 'components/MDSnackbar';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';

// Authentication layout components
import * as Func from 'services/Function';
import { FetchData } from 'services/Request';

function ProductImage({ parent, callback, size }) {
  const mod = 'product';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [notify, setNotify] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [list, setList] = useState([]);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const open = () => {
    setLoading(true);

    FetchData('OPTIONS', `/${mod}/image`, app.token, {
      lang: app.lang,
      id: parent.product_id,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        setList(result.image);
        setModalOpen(true);
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const renderList = () => {
    return (
      <MDBox sx={{ minHeight: 500 }}>
        <Grid container spacing={2}>
          {list.map((item) => (
            <Grid key={item.id} item xs={12} sm={6} md={4} xl={3}>
              <Paper sx={{ p: 2 }} variant='outlined'>
                <MDBox
                  component='img'
                  src={item.image}
                  alt=''
                  borderRadius='lg'
                  shadow='sm'
                  width='100%'
                  height='100%'
                  position='relative'
                  zIndex={10}
                />
                <MDBox mt={2} display='flex' justifyContent='center'>
                  <MDButton
                    variant='gradient'
                    color='light'
                    size='small'
                    component='span'
                    onClick={() => {
                      setModalOpen(false);
                      callback({
                        id: parent.id,
                        image_id: item.id,
                      });
                    }}
                  >
                    <Icon>push_pin</Icon>&nbsp;เลือกรูปภาพ
                  </MDButton>
                </MDBox>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </MDBox>
    );
  };

  const renderOpen = () => {
    return (
      <Dialog
        open={modalOpen}
        maxWidth='xxl'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={() => setModalOpen(false)}
      >
        <DialogTitle>
          เลือกรูปภาพ
          <IconButton
            aria-label='close'
            onClick={() => setModalOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{renderList()}</DialogContent>
      </Dialog>
    );
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  return (
    <MDBox>
      {size === 'large' ? (
        <button onClick={open} type='button' className='button'>
          <MDBox
            component='img'
            src={parent.image}
            alt=''
            width='100%'
            height={200}
            sx={{ borderRadius: 3, overflow: 'hidden' }}
          />
        </button>
      ) : (
        <button onClick={open} type='button' className='button'>
          {Func.imageDisplay(parent)}
        </button>
      )}
      {renderOpen()}
      {renderLoading()}
      {notify}
    </MDBox>
  );
}

ProductImage.defaultProps = {
  size: 'small',
};

ProductImage.propTypes = {
  parent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    product_id: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
  }).isRequired,
  callback: PropTypes.func.isRequired,
  size: PropTypes.string,
};

export default ProductImage;
